import {
  discoveryApiRef,
  fetchApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { useCallback, useEffect, useRef, useState } from 'react';

export type OnCallSchedule = {
  scheduleId: string;
  scheduleName: string;
  teamName: string;
  recipients: string[];
  myNextOnCall?: { start: string; end: string };
  nextRecipient?: string;
  nextStartAt?: string;
};

type FlatScheduleItem = {
  _parent: {
    id: string;
    name: string;
    enabled: boolean;
  };
  onCallRecipients: string[];
};

type TimelinePeriod = {
  startDate: string;
  endDate: string;
  recipient: { name: string; type?: string };
};

const CACHE_TTL = 60 * 60 * 1000;
let cached: { data: OnCallSchedule[]; ts: number } | undefined;

const LOG_PREFIX = '[useOnCall]';

function getUserGroups(ownershipEntityRefs: string[]): string[] {
  return ownershipEntityRefs
    .filter(ref => ref.startsWith('group:'))
    .map(ref => ref.replace(/^group:[^/]*\//, '').toLowerCase());
}

function normalizeScheduleStem(name: string): string {
  return name
    .replace(/_schedule$/i, '')
    .replace(/[_\s]/g, '-')
    .toLowerCase();
}

type UseOnCallResult = {
  value: OnCallSchedule[] | undefined;
  loading: boolean;
  error: Error | undefined;
  retry: () => void;
};

async function fetchWithRetry(
  fetchApi: { fetch: typeof fetch },
  url: string,
  attempts: number = 2,
): Promise<Response> {
  for (let i = 0; i < attempts; i++) {
    const res = await fetchApi.fetch(url);
    if (res.ok || i === attempts - 1) return res;
    console.warn(
      `${LOG_PREFIX} Request failed (attempt ${i + 1}/${attempts}): ${url} — ${
        res.status
      }`,
    );
    await new Promise(r => setTimeout(r, 1000 * (i + 1)));
  }
  throw new Error('unreachable');
}

export const useOnCall = (): UseOnCallResult => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const identityApi = useApi(identityApiRef);

  const [value, setValue] = useState<OnCallSchedule[] | undefined>(
    cached ? cached.data : undefined,
  );
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState<Error | undefined>();
  const mountedRef = useRef(true);
  const fetchIdRef = useRef(0);

  const load = useCallback(async () => {
    if (cached && Date.now() - cached.ts < CACHE_TTL) {
      setValue(cached.data);
      setLoading(false);
      setError(undefined);
      return;
    }

    const fetchId = ++fetchIdRef.current;
    setLoading(true);
    setError(undefined);

    try {
      const [identity, profile] = await Promise.all([
        identityApi.getBackstageIdentity(),
        identityApi.getProfileInfo(),
      ]);
      const userEmail = profile.email?.toLowerCase() ?? '';
      const groups = getUserGroups(identity.ownershipEntityRefs);
      if (!userEmail) {
        console.warn(
          `${LOG_PREFIX} No user email available — cannot match schedules`,
        );
        if (mountedRef.current && fetchId === fetchIdRef.current) {
          setValue([]);
          setLoading(false);
        }
        return;
      }

      console.debug(
        `${LOG_PREFIX} Fetching on-call schedules for ${userEmail}, groups: [${groups.join(
          ', ',
        )}]`,
      );

      const proxyBase = await discoveryApi.getBaseUrl('proxy');
      const res = await fetchWithRetry(
        fetchApi,
        `${proxyBase}/opsgenie/v2/schedules/on-calls?flat=true`,
      );

      if (!res.ok) {
        throw new Error(
          `OpsGenie schedules API returned ${res.status}: ${res.statusText}`,
        );
      }

      const json = await res.json();
      const items: FlatScheduleItem[] = json.data ?? [];
      const enabled = items.filter(s => s._parent.enabled);
      console.debug(
        `${LOG_PREFIX} Found ${enabled.length} enabled schedules (${items.length} total)`,
      );

      const schedules: OnCallSchedule[] = [];

      const relevant = enabled.filter(s => {
        const isCurrentlyOnCall = s.onCallRecipients.some(
          r => r.toLowerCase() === userEmail,
        );
        if (isCurrentlyOnCall) return true;

        const stem = normalizeScheduleStem(s._parent.name);
        return groups.some(g => stem === g || stem.includes(g));
      });

      console.debug(
        `${LOG_PREFIX} Filtered to ${
          relevant.length
        } relevant schedule(s) from ${
          enabled.length
        } enabled (groups: [${groups.join(', ')}])`,
      );

      const timelineResults = await Promise.allSettled(
        relevant.map(async s => {
          const schedule: OnCallSchedule = {
            scheduleId: s._parent.id,
            scheduleName: s._parent.name,
            teamName: s._parent.name
              .replace(/_schedule$/i, '')
              .replace(/_/g, ' '),
            recipients: s.onCallRecipients,
          };

          const isCurrentlyOnCall = s.onCallRecipients.some(
            r => r.toLowerCase() === userEmail,
          );
          if (isCurrentlyOnCall) {
            schedule.myNextOnCall = { start: 'now', end: '' };
          }

          const timelineRes = await fetchWithRetry(
            fetchApi,
            `${proxyBase}/opsgenie/v2/schedules/${s._parent.id}/timeline?interval=30&intervalUnit=days`,
          );
          if (!timelineRes.ok) {
            console.warn(
              `${LOG_PREFIX} Timeline request failed for "${s._parent.name}": ${timelineRes.status}`,
            );
            return schedule;
          }

          const timelineJson = await timelineRes.json();
          const rotations = timelineJson.data?.finalTimeline?.rotations ?? [];
          const allPeriods: TimelinePeriod[] = rotations.flatMap(
            (rot: { periods?: TimelinePeriod[] }) => rot.periods ?? [],
          );
          const now = Date.now();
          const currentEmails = new Set(
            s.onCallRecipients.map(r => r.toLowerCase()),
          );

          // Find current user's next on-call shift (if not already on-call)
          if (!isCurrentlyOnCall) {
            for (const p of allPeriods) {
              if ((p.recipient?.name?.toLowerCase() ?? '') === userEmail) {
                schedule.myNextOnCall = { start: p.startDate, end: p.endDate };
                break;
              }
            }
          }

          // Find next on-call person (first future period with a different recipient)
          const nextPeriod = allPeriods
            .filter(p => new Date(p.startDate).getTime() > now)
            .sort(
              (a, b) =>
                new Date(a.startDate).getTime() -
                new Date(b.startDate).getTime(),
            )
            .find(
              p => !currentEmails.has(p.recipient?.name?.toLowerCase() ?? ''),
            );
          if (nextPeriod) {
            schedule.nextRecipient = nextPeriod.recipient.name;
            schedule.nextStartAt = nextPeriod.startDate;
          }

          return schedule;
        }),
      );

      const failedCount = timelineResults.filter(
        r => r.status === 'rejected',
      ).length;
      if (failedCount > 0) {
        console.warn(`${LOG_PREFIX} ${failedCount} timeline request(s) failed`);
      }

      for (const result of timelineResults) {
        if (result.status === 'fulfilled' && result.value) {
          schedules.push(result.value);
        }
      }

      console.debug(
        `${LOG_PREFIX} Matched ${schedules.length} schedule(s) for user's teams`,
      );

      cached = { data: schedules, ts: Date.now() };
      if (mountedRef.current && fetchId === fetchIdRef.current) {
        setValue(schedules);
        setLoading(false);
      }
    } catch (err) {
      console.error(`${LOG_PREFIX} Failed to fetch on-call data:`, err);
      if (mountedRef.current && fetchId === fetchIdRef.current) {
        if (cached) {
          console.debug(`${LOG_PREFIX} Serving stale cache after error`);
          setValue(cached.data);
        }
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
      }
    }
  }, [discoveryApi, fetchApi, identityApi]);

  useEffect(() => {
    mountedRef.current = true;
    load();
    return () => {
      mountedRef.current = false;
    };
  }, [load]);

  return { value, loading, error, retry: load };
};
